import React, { useState } from 'react';
import { useGraduateList } from '@/hooks/queries/graduate/useList';
import { useGraduateCreate, useGraduateUpdate, useGraduateDelete } from '@/hooks/mutations/graduate';
import { Graduate, GraduateInput } from '@/types/graduates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Edit2, Trash2, GraduationCap } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { FileUploadInput } from '@/components/FileUploadInput';
import { getMediaUrl } from '@/utils/common';
import { Pagination } from '@/components/Pagination';

const GraduatesPage = () => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedGraduate, setSelectedGraduate] = useState<Graduate | null>(null);

  const [formData, setFormData] = useState<GraduateInput>({
    photo: '',
    translations: [
      { locale: 'uz', fullname: '', company: '', position: '', title: '', subtitle: '' },
      { locale: 'ru', fullname: '', company: '', position: '', title: '', subtitle: '' },
      { locale: 'en', fullname: '', company: '', position: '', title: '', subtitle: '' },
    ],
  });

  const { data: graduatesData, isLoading, refetch } = useGraduateList({ pageNumber: page, pageSize: 20 });
  const { mutate: createGraduate, isPending: isCreating } = useGraduateCreate();
  const { mutate: updateGraduate, isPending: isUpdating } = useGraduateUpdate();
  const { mutate: deleteGraduate } = useGraduateDelete();

  const handleOpenCreate = () => {
    setSelectedGraduate(null);
    setFormData({
      photo: '',
      translations: [
        { locale: 'uz', fullname: '', company: '', position: '', title: '', subtitle: '' },
        { locale: 'ru', fullname: '', company: '', position: '', title: '', subtitle: '' },
        { locale: 'en', fullname: '', company: '', position: '', title: '', subtitle: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (graduate: Graduate) => {
    setSelectedGraduate(graduate);
    setFormData({
      photo: graduate.photo || '',
      courseId: graduate.courseId,
      translations: [
        {
          locale: 'uz',
          fullname: graduate.fullname || '',
          company: graduate.company || '',
          position: graduate.position || '',
          title: graduate.title || '',
          subtitle: graduate.subtitle || '',
        },
        { locale: 'ru', fullname: '', company: '', position: '', title: '', subtitle: '' },
        { locale: 'en', fullname: '', company: '', position: '', title: '', subtitle: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleTranslationChange = (
    locale: 'uz' | 'ru' | 'en',
    field: 'fullname' | 'company' | 'position' | 'title' | 'subtitle',
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) => (t.locale === locale ? { ...t, [field]: value } : t)),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedGraduate) {
      updateGraduate(
        { id: selectedGraduate.id, data: formData },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Bitiruvchi yangilandi' });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Bitiruvchi yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createGraduate(formData, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Yangi bitiruvchi hikoyasi yaratildi' });
          setIsModalOpen(false);
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Bitiruvchi yaratilmadi', variant: 'destructive' }),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedGraduate) return;
    deleteGraduate(selectedGraduate.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Bitiruvchi o'chirildi" });
        setIsDeleteOpen(false);
        setSelectedGraduate(null);
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: "Bitiruvchi o'chirilmadi", variant: 'destructive' }),
    });
  };

  const graduatesList: Graduate[] = graduatesData?.data?.data || graduatesData?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Bitiruvchilar</h1>
          <p className="text-sm text-slate-500 mt-1">Bitiruvchilar va ularning muvaffaqiyat hikoyalari</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="size-4" /> Yangi bitiruvchi
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden p-6 space-y-6">
        {isLoading ? (
          <div className="py-12"><Loader /></div>
        ) : graduatesList.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <GraduationCap className="size-12 mx-auto text-slate-300 mb-3" />
            <p>Hozircha bitiruvchilar mavjud emas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50/75 text-xs uppercase font-semibold text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3.5">Rasm</th>
                  <th className="px-4 py-3.5">F.I.SH</th>
                  <th className="px-4 py-3.5">Kompaniya</th>
                  <th className="px-4 py-3.5">Lavozim</th>
                  <th className="px-4 py-3.5">Hikoya Sarlavhasi</th>
                  <th className="px-4 py-3.5 text-right">Amallar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {graduatesList.map((grad) => (
                  <tr key={grad.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3">
                      {grad.photo ? (
                        <img src={getMediaUrl(grad.photo)} alt={grad.fullname} className="size-10 rounded-full object-cover border border-slate-200" />
                      ) : (
                        <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                          {grad.fullname?.[0] || 'B'}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{grad.fullname || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{grad.company || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{grad.position || '—'}</td>
                    <td className="px-4 py-3 max-w-xs truncate text-slate-500">{grad.title || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(grad)} className="size-8 text-slate-600 hover:text-primary">
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedGraduate(grad);
                            setIsDeleteOpen(true);
                          }}
                          className="size-8 text-slate-600 hover:text-red-500"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
<Pagination className="mt-4" currentPage={page} setCurrentPage={setPage} paginationInfo={graduatesData?.meta?.pagination ?? graduatesData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedGraduate ? 'Bitiruvchini tahrirlash' : 'Yangi bitiruvchi yaratish'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <FileUploadInput
              label="Rasm (Photo)"
              value={formData.photo}
              onChange={(url) => setFormData({ ...formData, photo: url })}
            />

            <div className="pt-2">
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Ko'p tilli ma'lumotlar</Label>
              <Tabs defaultValue="uz" className="w-full">
                <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha (UZ)</TabsTrigger>
                  <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский (RU)</TabsTrigger>
                  <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English (EN)</TabsTrigger>
                </TabsList>

                {(['uz', 'ru', 'en'] as const).map((locale) => {
                  const translation = formData.translations.find((t) => t.locale === locale) || {
                    fullname: '',
                    company: '',
                    position: '',
                    title: '',
                    subtitle: '',
                  };
                  return (
                    <TabsContent key={locale} value={locale} className="space-y-3 pt-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs text-slate-600">F.I.SH ({locale.toUpperCase()})</Label>
                          <Input
                            value={translation.fullname || ''}
                            onChange={(e) => handleTranslationChange(locale, 'fullname', e.target.value)}
                            placeholder="Jasur Toshmatov"
                            className="rounded-xl"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-slate-600">Kompaniya ({locale.toUpperCase()})</Label>
                          <Input
                            value={translation.company || ''}
                            onChange={(e) => handleTranslationChange(locale, 'company', e.target.value)}
                            placeholder="Uzum Market"
                            className="rounded-xl"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Lavozim ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.position || ''}
                          onChange={(e) => handleTranslationChange(locale, 'position', e.target.value)}
                          placeholder="Backend Developer"
                          className="rounded-xl"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Hikoya Sarlavhasi ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.title || ''}
                          onChange={(e) => handleTranslationChange(locale, 'title', e.target.value)}
                          placeholder="Men 01 da o'qib dasturchi bo'ldim"
                          className="rounded-xl"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Qo'shimcha sarlavha ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.subtitle || ''}
                          onChange={(e) => handleTranslationChange(locale, 'subtitle', e.target.value)}
                          placeholder="6 oyda kasb egallash mumkin"
                          className="rounded-xl"
                        />
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Bitiruvchini o'chirishni tasdiqlaysizmi?"
        alertDescription="Ushbu bitiruvchi tizimdan butunlay o'chiriladi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};

export default GraduatesPage;
